/**
 * Apple Ads → 策略配置（CLAUDE.md §前端 UI 须借鉴 LEO v1.0；本页复用 leo-* 类）。
 *
 * 已读 docs/frontend_ui_guidline/leo-frontend-design.md v1.0（§6.1 按钮层级
 * （主 CTA 橙渐变每区块唯一）；§6.3 表单控件（输入深底 + focus 橙；药丸 Toggle
 * is-on 填充强调色）；§6.5 表格（L4 表头 text-muted、行分隔 border-white/5、
 * 状态徽章语义描边色）；§6.6 弹窗（遮罩 + 居中面板）；§2.3 颜色使用规则）。
 *
 * 行为：
 *   - 「目标费率与成本」卡片（PR-AT-20260921-005 初版目标费率；PR-AT-20260923-012
 *       扩目标成本 cost_value，auto_apple_feilv_target_config）：
 *       · 进入页面 GET /api/v1/apple-asa/feilv-target-config 拉取当前态
 *         （表无行 / DDL 未执行时后端 fail-open 返回默认态 custom + 0.01 + 成本 4000）；
 *       · 目标费率当前仅 custom 手动固定值模式（(0,1) 开区间小数，如 0.01=1%）；
 *       · 目标成本 = 目标企业进件成本（元，(0,20000) 开区间，默认 4000，
 *         与盯盘指标 cost_ent_appl 同口径），供后续策略阈值消费；
 *       · 权限键 apple:feilv-target，无权限用户控件 disabled 并提示只读。
 *   - 「自定义策略配置」卡片（PR-AT-20260922-006，auto_apple_watch_strategy_config）：
 *       · GET /api/v1/apple-asa/watch-strategy 拉取策略列表（按 id 倒序，任意登录
 *         用户可读）；
 *       · 列表列：状态（药丸开关，点击调 PUT /{id}/status）/ 策略id
 *         （auto_apple_watch_strategy_config.id 自增主键，等宽弱化展示）/
 *         满足条件（条件组中文
 *         文本，多条件换行以「且」连接）/ 执行操作（「出价降低 10%」「暂停投放」
 *         「预警」徽章）/ 创建者（create_user + created_time）/ 操作（编辑 + 删除：
 *         删除为软删除 is_deleted=1，先弹确认框防误触，PR-AT-20261102-001）；
 *       · 阈值模式（PR-AT-20260923-009；011 起扩至 total_sxfl）：sxfl / total_sxfl
 *         授信费率类条件的阈值可选「目标费率×x」，列表「满足条件」列渲染为
 *         「授信费率>目标费率×1.2(=0.012)」（= 后为按当前目标费率换算的生效阈值），
 *         编辑弹层对应行内同步展示实时换算；
 *       · 「新建盯盘服务」按钮打开 WatchStrategyEditor 弹层（本区块唯一主 CTA），
 *         编辑复用同弹层回显；新建默认打开，编辑不动开关态与首次创建人；
 *       · 列表/脑图双视图（PR-AT-20261102-002，镜像华为页脑图）：默认列表视图；
 *         「脑图」视图把全量策略合并为「条件前缀树 → 执行操作」（同层完全相同结点
 *         合并为一个，标注「启用 x/共 y」，全停用子树弱化），飞书画板式盒节点 +
 *         贝塞尔连线、默认展开根以下 4 层、画布可拖拽平移、支持整屏全屏查看；
 *         操作叶子追加「策略id」行（与列表列对账）；数值型条件与出价调/提叶子的
 *         数值支持内联编辑（确认弹窗批量应用合并结点途经的全部策略），无权限
 *         用户保持纯预览（WatchStrategyMindMap）；
 *       · 权限键 apple:watch-strategy（用户组同 apple:redline），无权限时按钮/控件
 *         disabled + 黄色提示条；后端写接口同样校验（403/400/404/500 兜底）；
 *       · 生效链路：开启的策略由后端判断引擎脚本（apple-watch-strategy，周期性外部
 *         调度）按关键词粒度判断，命中结果落库 t_apple_watch_strategy_result
 *         （本轮只判断 + 存储，不调 Apple API 执行改价/暂停）。
 *
 * 与腾讯 StrategyConfigPage 的差异：apple 域无「广告操作开关」卡片（无对应后端表）。
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../services/apiClient";
import {
  type FeilvTargetConfigRecord,
  getFeilvTargetConfig,
  saveFeilvTargetConfig,
} from "../../services/apple/feilvTargetConfig";
import {
  type SaveWatchStrategyBody,
  type WatchStrategyRecord,
  createWatchStrategy,
  deleteWatchStrategy,
  describeAction,
  describeCondition,
  listWatchStrategies,
  toggleWatchStrategy,
  updateWatchStrategy,
} from "../../services/apple/watchStrategy";
import WatchStrategyEditor from "../../components/appleWatchStrategy/WatchStrategyEditor";
import WatchStrategyMindMap from "../../components/appleWatchStrategy/WatchStrategyMindMap";
import {
  type MindMapNode,
  validateNodeValue,
  buildConditionValuePatchBodies,
  buildActionValuePatchBodies,
} from "../../components/appleWatchStrategy/strategyMindMap";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { useAuth } from "../../contexts/AuthContext";
import { PAGE_KEYS } from "../../config/permissions";
import { pushToast } from "../../hooks/useToast";

/** 库内态缺失（GET 失败/未加载）时的默认基线值（与后端 DEFAULT_FEILV_VALUE 一致）。 */
const DEFAULT_FEILV_VALUE = 0.01;

/** 库内态缺失时的默认目标企业进件成本（元，与后端 DEFAULT_COST_VALUE 一致）。 */
const DEFAULT_COST_VALUE = 4000;

/** 执行操作徽章样式（L7 行内操作，语义描边色，仿华为页 actionTagClass）。 */
function watchActionTagClass(actionType: string): string {
  if (actionType === "pause_keyword") return "leo-tag is-danger";
  if (actionType === "reduce_bid" || actionType === "alert") return "leo-tag is-warning";
  return "leo-tag is-info";
}

export default function StrategyConfigPage() {
  const { canEdit } = useAuth();
  // 目标费率：独立权限键 apple:feilv-target（用户组同 apple:redline + 管理员）。
  const canOperateFeilv = canEdit(PAGE_KEYS.APPLE_FEILV_TARGET);
  // 自定义策略配置：独立权限键 apple:watch-strategy（用户组同 apple:redline + 管理员）。
  const canOperateWatch = canEdit(PAGE_KEYS.APPLE_WATCH_STRATEGY);

  const [loading, setLoading] = useState(true);

  // 目标费率与成本配置：record 为库内当前态；valueDraft / costDraft 为本地编辑态。
  const [feilvRecord, setFeilvRecord] = useState<FeilvTargetConfigRecord | null>(null);
  const [feilvValueDraft, setFeilvValueDraft] = useState(String(DEFAULT_FEILV_VALUE));
  const [costValueDraft, setCostValueDraft] = useState(String(DEFAULT_COST_VALUE));
  const [feilvSaving, setFeilvSaving] = useState(false);

  // 自定义盯盘策略：列表 + 弹层编辑态。
  const [watchItems, setWatchItems] = useState<WatchStrategyRecord[]>([]);
  const [watchLoading, setWatchLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<WatchStrategyRecord | null>(null);
  const [editorSaving, setEditorSaving] = useState(false);
  /** 正在切换开关的策略 id（防重复点击）。 */
  const [togglingId, setTogglingId] = useState<number | null>(null);
  /** 删除确认弹窗：待删策略行 + 提交中标记（PR-AT-20261102-001）。 */
  const [deletingWatch, setDeletingWatch] = useState<WatchStrategyRecord | null>(null);
  const [watchDeleting, setWatchDeleting] = useState(false);
  /** 策略视图：list=六列表格（编辑主链路）；mindmap=条件前缀树脑图（数值可内联编辑，
   *  PR-AT-20261102-002，镜像华为页）。 */
  const [strategyView, setStrategyView] = useState<"list" | "mindmap">("list");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getFeilvTargetConfig();
      setFeilvRecord(r);
      if (r) {
        setFeilvValueDraft(String(r.feilv_value));
        setCostValueDraft(String(r.cost_value));
      }
    } catch (err) {
      pushToast(
        err instanceof ApiError ? err.message : "拉取目标费率与成本配置失败",
        "error",
      );
      setFeilvRecord(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const reloadWatch = useCallback(async () => {
    setWatchLoading(true);
    try {
      const r = await listWatchStrategies();
      setWatchItems(r.items);
    } catch (err) {
      pushToast(
        err instanceof ApiError ? err.message : "拉取自定义盯盘策略失败",
        "error",
      );
      setWatchItems([]);
    } finally {
      setWatchLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    reloadWatch();
  }, [reload, reloadWatch]);

  async function onSaveFeilv() {
    // feilvRecord 允许为 null（GET 失败/未加载时按默认基线 custom+0.01+成本4000 保存），
    // 不因读取失败永久锁死保存按钮。
    if (feilvSaving || !canOperateFeilv) return;
    const parsed = Number.parseFloat(feilvValueDraft);
    if (!(Number.isFinite(parsed) && parsed > 0 && parsed < 1)) {
      pushToast("目标费率须为 (0,1) 开区间小数（如 0.01 表示 1%）", "error");
      return;
    }
    const parsedCost = Number.parseFloat(costValueDraft);
    if (!(Number.isFinite(parsedCost) && parsedCost > 0 && parsedCost < 20000)) {
      pushToast("目标成本须为 (0,20000) 开区间的金额（元，如 4000）", "error");
      return;
    }
    setFeilvSaving(true);
    try {
      const r = await saveFeilvTargetConfig({
        feilv_mode: "custom",
        feilv_value: parsed,
        cost_value: parsedCost,
      });
      setFeilvRecord(r);
      setFeilvValueDraft(String(r.feilv_value));
      setCostValueDraft(String(r.cost_value));
      pushToast(
        `目标费率已保存为固定值 ${r.feilv_value}，目标成本 ${r.cost_value} 元`,
        "success",
      );
    } catch (err) {
      pushToast(
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "保存目标费率与成本配置失败",
        "error",
      );
    } finally {
      setFeilvSaving(false);
    }
  }

  function onCreateWatch() {
    setEditingRecord(null);
    setEditorOpen(true);
  }

  function onEditWatch(record: WatchStrategyRecord) {
    setEditingRecord(record);
    setEditorOpen(true);
  }

  /** 弹层保存：新建 POST / 编辑 PUT；成功后将返回的当前态并入列表，避免整表重拉。 */
  async function onSubmitWatch(body: SaveWatchStrategyBody) {
    if (editorSaving) return;
    setEditorSaving(true);
    try {
      const saved = editingRecord
        ? await updateWatchStrategy(editingRecord.id, body)
        : await createWatchStrategy(body);
      setWatchItems((prev) =>
        editingRecord
          ? prev.map((it) => (it.id === saved.id ? saved : it))
          : [saved, ...prev],
      );
      setEditorOpen(false);
      setEditingRecord(null);
      pushToast(editingRecord ? "盯盘服务已更新" : "盯盘服务已新建（默认打开）", "success");
    } catch (err) {
      pushToast(
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "保存盯盘服务失败",
        "error",
      );
    } finally {
      setEditorSaving(false);
    }
  }

  /**
   * 脑图数值内联编辑提交（PR-AT-20261102-002，镜像华为页）：合并结点 = 批量应用——
   * 对途经（条件结点）/ 归属（操作叶子）该结点的全部策略逐条 PUT 更新对应数值，
   * 条件组其余字段与操作原样；结束后重拉全量（脑图树自动重建）。
   */
  async function handleMindMapValueSubmit(node: MindMapNode, newValue: number) {
    // 组件已做一轮校验，此处防御性复核（非法直接 toast，不产生任何请求）
    const err = validateNodeValue(node, newValue);
    if (err) {
      pushToast(err, "error");
      return;
    }
    const patches =
      node.editable?.kind === "condition_value"
        ? buildConditionValuePatchBodies(node, newValue)
        : buildActionValuePatchBodies(node, newValue);
    const results = await Promise.allSettled(
      patches.map((p) => updateWatchStrategy(p.id, p.body)),
    );
    const okCount = results.filter((r) => r.status === "fulfilled").length;
    const failCount = results.length - okCount;
    if (failCount === 0) {
      pushToast(`已更新 ${okCount} 条策略的数值`, "success");
    } else {
      pushToast(`数值更新完成：成功 ${okCount} 条，失败 ${failCount} 条`, "error");
    }
    await reloadWatch();
  }

  /** 删除策略（DELETE /{id}，软删除）：确认弹窗 → 删除成功后本地剔除该行（不重拉全量）。 */
  async function onDeleteWatch() {
    if (!deletingWatch || watchDeleting || !canOperateWatch) return;
    setWatchDeleting(true);
    try {
      await deleteWatchStrategy(deletingWatch.id);
      setWatchItems((prev) => prev.filter((it) => it.id !== deletingWatch.id));
      pushToast(`盯盘策略 #${deletingWatch.id} 已删除`, "success");
      setDeletingWatch(null);
    } catch (err) {
      pushToast(
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "删除盯盘策略失败",
        "error",
      );
    } finally {
      setWatchDeleting(false);
    }
  }

  /** 打开 / 关闭策略（PUT /{id}/status），成功后用返回的当前态更新本地行。 */
  async function onToggleWatch(record: WatchStrategyRecord) {
    if (!canOperateWatch || togglingId !== null) return;
    setTogglingId(record.id);
    try {
      const saved = await toggleWatchStrategy(record.id, !record.is_enabled);
      setWatchItems((prev) => prev.map((it) => (it.id === saved.id ? saved : it)));
      pushToast(saved.is_enabled ? `策略 #${saved.id} 已打开` : `策略 #${saved.id} 已关闭`, "success");
    } catch (err) {
      pushToast(
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "切换策略状态失败",
        "error",
      );
    } finally {
      setTogglingId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-[13px] text-text-muted">加载中…</div>
      </div>
    );
  }

  // ----- 目标费率与成本派生态：固定值合法性 / 是否有未保存修改 -----
  const parsedDraft = Number.parseFloat(feilvValueDraft);
  const isDraftValid =
    Number.isFinite(parsedDraft) && parsedDraft > 0 && parsedDraft < 1;
  const feilvInvalid = !isDraftValid;
  const parsedCostDraft = Number.parseFloat(costValueDraft);
  const isCostDraftValid =
    Number.isFinite(parsedCostDraft) && parsedCostDraft > 0 && parsedCostDraft < 20000;
  const costInvalid = !isCostDraftValid;
  // 比对基线：库内态缺失（GET 失败/未加载）时按默认态 0.01 / 4000，
  // 保证「做修改后即可保存」不被读取失败锁死。
  const feilvBaselineValue = feilvRecord?.feilv_value ?? DEFAULT_FEILV_VALUE;
  const costBaselineValue = feilvRecord?.cost_value ?? DEFAULT_COST_VALUE;
  const feilvDirty = isDraftValid && parsedDraft !== feilvBaselineValue;
  const costDirty = isCostDraftValid && parsedCostDraft !== costBaselineValue;
  const configDirty = feilvDirty || costDirty;
  const configInvalid = feilvInvalid || costInvalid;

  const thClass = "px-2.5 py-2 text-left font-medium text-text-muted whitespace-nowrap";
  const tdClass = "px-2.5 py-2 text-text-body-strong";

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-7 md:px-10 md:py-9 lg:px-14">
      <div className="mb-5">
        <h1 className="font-display text-[1.55rem] font-bold leading-tight text-text-emphasis">
          策略配置
        </h1>
      </div>

      {/* 目标费率与成本卡片（LEO §6.3 表单控件：leo-input 深底 + focus 橙；保存为本卡片
          唯一主 CTA §6.1）。 */}
      <div className="leo-card rounded-xl p-6">
        <div className="min-w-0">
          <div className="font-display text-[1.05rem] font-semibold text-text-emphasis">
            目标费率与成本
          </div>
          <div className="mt-1.5 text-[12.5px] text-text-muted">
            Apple Ads 程序化控停策略的目标配置（feilv_target）。目标费率手动输入固定值
            （(0,1) 开区间小数，如 0.01 表示 1%）；目标成本 = 目标企业进件成本
            （元，(0,20000) 开区间，与盯盘指标 cost_ent_appl 同口径）。
          </div>
        </div>

        {/* 「目标费率：[输入框]」「目标成本：[输入框] 元」固定同行：子组 shrink-0 内部
            不换行；输入框经定宽容器包裹——.leo-input 的 width:100% 在构建产物中位于
            Tailwind w-* 之后（同优先级后者胜），直接设宽度会被撑满整行。 */}
        <div className="mt-4 flex flex-wrap items-center gap-x-2.5 gap-y-2">
          <div className="flex shrink-0 items-center gap-2.5">
            <span className="text-[12.5px] text-text-muted">目标费率：</span>
            <div className="w-28 shrink-0">
              <input
                type="text"
                inputMode="decimal"
                className="leo-input"
                value={feilvValueDraft}
                placeholder="如 0.01"
                readOnly={!canOperateFeilv}
                onChange={(e) => setFeilvValueDraft(e.target.value)}
                aria-label="目标费率"
              />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2.5">
            <span className="text-[12.5px] text-text-muted">目标成本：</span>
            <div className="w-28 shrink-0">
              <input
                type="text"
                inputMode="decimal"
                className="leo-input"
                value={costValueDraft}
                placeholder="如 4000"
                readOnly={!canOperateFeilv}
                onChange={(e) => setCostValueDraft(e.target.value)}
                aria-label="目标成本（元）"
              />
            </div>
            <span className="text-[12.5px] text-text-muted">元</span>
          </div>
          <button
            type="button"
            className="leo-cta-primary ml-1 w-auto px-7"
            onClick={onSaveFeilv}
            disabled={!canOperateFeilv || feilvSaving || !configDirty || configInvalid}
          >
            {feilvSaving ? "保存中…" : "保存"}
          </button>
          {feilvInvalid ? (
            <span className="text-[11.5px] text-semantic-danger">
              目标费率须为 (0,1) 开区间小数（如 0.01）
            </span>
          ) : null}
          {costInvalid ? (
            <span className="text-[11.5px] text-semantic-danger">
              目标成本须为 (0,20000) 开区间（元，如 4000）
            </span>
          ) : null}
        </div>

        {/* 审计行 */}
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line-subtle pt-4 text-[12px] text-text-muted">
          {feilvRecord?.modify_user ? (
            <span>
              最近修改：<span className="text-text-strong">{feilvRecord.modify_user}</span>
              <span className="mx-1.5 text-text-muted-weak">·</span>
              <span className="font-mono">{feilvRecord.updated_time ?? "—"}</span>
            </span>
          ) : (
            <span>尚无修改记录（默认 custom 0.01 / 成本 4000 元）</span>
          )}
          {feilvRecord?.create_user ? (
            <span>
              首次创建：<span className="text-text-strong">{feilvRecord.create_user}</span>
            </span>
          ) : null}
        </div>

        {/* 权限提示 */}
        {!canOperateFeilv ? (
          <div className="mt-4 rounded-md border border-semantic-warning/30 bg-semantic-warning/10 px-3 py-2 text-[12px] text-semantic-warning">
            仅 v_summerxiao / reizhu / v_hooverhuang / admin / devinzhang
            可操作目标费率与成本；当前账号为只读。
          </div>
        ) : null}
      </div>

      {/* 自定义策略配置卡片（PR-AT-20260922-006；「新建盯盘服务」为本区块唯一主 CTA）。 */}
      <div className="leo-card mt-5 rounded-xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display text-[1.05rem] font-semibold text-text-emphasis">
              自定义策略配置
            </div>
            <div className="mt-1.5 text-[12.5px] text-text-muted">
              自助配置盯盘服务：满足全部条件（且）时对命中关键词执行操作（出价降低 /
              出价提升 / 暂停投放 / 预警）。仅「打开」状态的策略参与判断，命中结果落库
              <span className="font-mono"> t_apple_watch_strategy_result</span>
              （本轮只判断 + 存储，不实际执行；预警仅记录命中供后续告警）。
            </div>
          </div>
          <button
            type="button"
            className="leo-cta-primary w-auto px-5 py-2 text-[13px]"
            onClick={onCreateWatch}
            disabled={!canOperateWatch}
          >
            新建盯盘服务
          </button>
        </div>

        {/* 权限提示 */}
        {!canOperateWatch ? (
          <div className="mt-4 rounded-md border border-semantic-warning/30 bg-semantic-warning/10 px-3 py-2 text-[12px] text-semantic-warning">
            仅 v_summerxiao / reizhu / v_hooverhuang / admin / devinzhang
            可配置盯盘服务；当前账号为只读。
          </div>
        ) : null}

        {/* 列表/脑图分段切换（LEO §6.2：.leo-toptab pill；独立一行避免挤压标题栏，
            镜像华为页 PR-HW-20261013-002） */}
        <div
          className="mt-4 flex w-fit items-center gap-0.5 rounded-full bg-da-inset p-0.5"
          role="tablist"
          aria-label="策略视图切换"
        >
          <button
            type="button"
            role="tab"
            aria-selected={strategyView === "list"}
            className={`leo-toptab ${strategyView === "list" ? "is-active" : ""}`}
            onClick={() => setStrategyView("list")}
          >
            列表
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={strategyView === "mindmap"}
            className={`leo-toptab ${strategyView === "mindmap" ? "is-active" : ""}`}
            onClick={() => setStrategyView("mindmap")}
          >
            脑图
          </button>
        </div>

        {/* 策略列表 / 脑图（LEO §6.5：表头 L4 text-muted、行分隔 border-white/5、禁浅色斑马纹；
            脑图仅在非 loading 且非空时渲染，loading/空态沿用表格行内提示） */}
        <div className="mt-4 overflow-x-auto">
          {strategyView === "mindmap" && !watchLoading && watchItems.length > 0 ? (
            /* 脑图视图：条件前缀树 → 执行操作（基线口径与表格列一致；数值型条件与
               出价调/提叶子的数值支持内联编辑，批量应用合并结点途经的全部策略） */
            <WatchStrategyMindMap
              strategies={watchItems}
              targetSxfl={feilvBaselineValue}
              targetCost={costBaselineValue}
              canEditValue={canOperateWatch}
              onSubmitNodeValue={handleMindMapValueSubmit}
            />
          ) : (
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-line-strong">
                <th className={thClass}>状态</th>
                <th className={thClass}>策略id</th>
                <th className={`${thClass} min-w-[320px]`}>满足条件</th>
                <th className={thClass}>执行操作</th>
                <th className={thClass}>创建者</th>
                <th className={thClass}>操作</th>
              </tr>
            </thead>
            <tbody>
              {watchLoading ? (
                <tr>
                  <td colSpan={5} className="px-2.5 py-8 text-center text-[12.5px] text-text-muted">
                    加载中…
                  </td>
                </tr>
              ) : watchItems.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-2.5 py-8 text-center text-[12.5px] text-text-muted">
                    尚未配置盯盘服务，可点击右上角「新建盯盘服务」创建
                  </td>
                </tr>
              ) : (
                watchItems.map((it) => {
                  const toggleDisabled = !canOperateWatch || togglingId !== null;
                  return (
                    <tr key={it.id} className="border-b border-white/5 last:border-b-0">
                      {/* 状态：药丸 Toggle（LEO §6.3：is-on 填充强调色橙）。 */}
                      <td className="px-2.5 py-2">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={it.is_enabled}
                          aria-label={`策略 #${it.id} 开关`}
                          onClick={() => onToggleWatch(it)}
                          disabled={toggleDisabled}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-150 ${
                            it.is_enabled ? "bg-accent-cta" : "bg-text-muted-weak"
                          } ${toggleDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-150 ${
                              it.is_enabled ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </td>
                      {/* 策略id：auto_apple_watch_strategy_config.id（自增主键），等宽字体便于与库表核对。 */}
                      <td className="px-2.5 py-2 font-mono text-text-muted whitespace-nowrap">
                        {it.id}
                      </td>
                      {/* 满足条件：条件组中文文本，多条件换行以「且」连接；
                          倍率模式按当前目标费率 / 目标成本追加生效阈值换算
                          「(=0.012)」「(=6000)」（PR-AT-20260923-013）。 */}
                      <td className={`${tdClass} whitespace-normal`}>
                        <div className="flex flex-col gap-0.5">
                          {it.conditions.map((c, i) => (
                            <span key={i}>
                              {i > 0 ? (
                                <span className="mr-1 text-text-muted-weak">且</span>
                              ) : null}
                              {describeCondition(c, feilvBaselineValue, costBaselineValue)}
                            </span>
                          ))}
                        </div>
                      </td>
                      {/* 执行操作：语义徽章。 */}
                      <td className="px-2.5 py-2 whitespace-nowrap">
                        <span className={watchActionTagClass(it.action_type)}>
                          {describeAction(it.action_type, it.action_value ?? null)}
                        </span>
                      </td>
                      {/* 创建者：create_user + created_time。 */}
                      <td className="px-2.5 py-2 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-text-strong">{it.create_user ?? "—"}</span>
                          <span className="font-mono text-[11px] text-text-muted">
                            {it.created_time ?? "—"}
                          </span>
                        </div>
                      </td>
                      {/* 操作：编辑 + 删除（软删除，删除前弹确认框防误触）。 */}
                      <td className="px-2.5 py-2 whitespace-nowrap">
                        <button
                          type="button"
                          className="leo-btn-ghost px-3 py-1.5 text-[12px]"
                          onClick={() => onEditWatch(it)}
                          disabled={!canOperateWatch}
                        >
                          编辑
                        </button>
                        <button
                          type="button"
                          className="leo-btn-ghost is-danger ml-1.5 px-3 py-1.5 text-[12px]"
                          onClick={() => setDeletingWatch(it)}
                          disabled={!canOperateWatch}
                        >
                          删除
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          )}
        </div>
      </div>

      <WatchStrategyEditor
        open={editorOpen}
        record={editingRecord}
        saving={editorSaving}
        targetFeilv={feilvBaselineValue}
        targetCost={costBaselineValue}
        onSubmit={onSubmitWatch}
        onCancel={() => {
          setEditorOpen(false);
          setEditingRecord(null);
        }}
      />

      {/* 删除确认弹窗（防误触；软删除，判断引擎不再消费该策略） */}
      <ConfirmDialog
        open={deletingWatch !== null}
        title="确认删除策略"
        description={
          <>
            确认删除策略{" "}
            <span className="font-mono font-semibold text-text-emphasis">
              #{deletingWatch?.id}
            </span>
            ？删除后判断引擎将不再消费该策略（软删除，库内保留记录可由管理员恢复）。
          </>
        }
        confirmText="确认删除"
        danger
        loading={watchDeleting}
        onConfirm={onDeleteWatch}
        onCancel={() => setDeletingWatch(null)}
      />
    </div>
  );
}
